/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Modal component
import MDModal from "components/MDModal";

function MDEditModal({ open, onClose, empresa, onSave }) {
  const [formData, setFormData] = useState({
    nombre: "",
    nit: "",
    email: "",
    telefono: "",
    descripcion: "",
    sector_empresa: "",
    tamaño_empresa: "",
    zona_estrategicas: "",
    area_operacion: "",
    pais_operacion: "",
    ciudad_operacion: "",
    estado_empresa: "",
  });

  // Opciones para los campos select
  const sectores = ["Tecnología", "Energía", "Alimentos", "Salud", "Educación", "Servicios", "Manufactura"];
  const tamaños = ["Pequeña", "Mediana", "Grande"];
  const zonas = ["Zona Norte", "Zona Centro", "Zona Sur", "Zona Este", "Zona Oeste"];
  const areas = ["Local", "Regional", "Nacional", "Internacional"];
  const estados = ["Activa", "Pendiente", "Inactiva"];

  useEffect(() => {
    if (empresa) {
      setFormData({
        nombre: empresa.nombre || "",
        nit: empresa.nit || "",
        email: empresa.email || "",
        telefono: empresa.telefono || "",
        descripcion: empresa.descripcion || "",
        sector_empresa: empresa.sector_empresa || "",
        tamaño_empresa: empresa.tamaño_empresa || "",
        zona_estrategicas: empresa.zona_estrategicas || "",
        area_operacion: empresa.area_operacion || "",
        pais_operacion: empresa.pais_operacion || "",
        ciudad_operacion: empresa.ciudad_operacion || "",
        estado_empresa: empresa.estado_empresa || "",
      });
    }
  }, [empresa]);

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSave = () => {
    onSave({ ...empresa, ...formData });
    onClose();
  };

  const handleCancel = () => {
    setFormData({
      nombre: "",
      nit: "",
      email: "",
      telefono: "",
      descripcion: "",
      sector_empresa: "",
      tamaño_empresa: "",
      zona_estrategicas: "",
      area_operacion: "",
      pais_operacion: "",
      ciudad_operacion: "",
      estado_empresa: "",
    });
    onClose();
  };

  return (
    <MDModal
      open={open}
      onClose={handleCancel}
      title="Editar Empresa"
      size="lg"
    >
      <MDBox p={3}>
        <Grid container spacing={3}>
          {/* Fila 1 */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nombre de la Empresa"
              value={formData.nombre}
              onChange={handleChange("nombre")}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="NIT"
              value={formData.nit}
              onChange={handleChange("nit")}
              variant="outlined"
            />
          </Grid>

          {/* Fila 2 */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange("email")}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Teléfono"
              value={formData.telefono}
              onChange={handleChange("telefono")}
              variant="outlined"
            />
          </Grid>

          {/* Fila 3 */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Descripción"
              multiline
              rows={3}
              value={formData.descripcion}
              onChange={handleChange("descripcion")}
              variant="outlined"
            />
          </Grid>

          {/* Fila 4 */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Sector / Industria"
              value={formData.sector_empresa}
              onChange={handleChange("sector_empresa")}
              variant="outlined"
            >
              {sectores.map((sector) => (
                <MenuItem key={sector} value={sector}>
                  {sector}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Tamaño de la Empresa"
              value={formData.tamaño_empresa}
              onChange={handleChange("tamaño_empresa")}
              variant="outlined"
            >
              {tamaños.map((tamaño) => (
                <MenuItem key={tamaño} value={tamaño}>
                  {tamaño}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Fila 5 */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Zona Estratégica"
              value={formData.zona_estrategicas}
              onChange={handleChange("zona_estrategicas")}
              variant="outlined"
            >
              {zonas.map((zona) => (
                <MenuItem key={zona} value={zona}>
                  {zona}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Área de Operación"
              value={formData.area_operacion}
              onChange={handleChange("area_operacion")}
              variant="outlined"
            >
              {areas.map((area) => (
                <MenuItem key={area} value={area}>
                  {area}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Fila 6 */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="País de Operación"
              value={formData.pais_operacion}
              onChange={handleChange("pais_operacion")}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Ciudad de Operación"
              value={formData.ciudad_operacion}
              onChange={handleChange("ciudad_operacion")}
              variant="outlined"
            />
          </Grid>

          {/* Fila 7 */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Estado de la Empresa"
              value={formData.estado_empresa}
              onChange={handleChange("estado_empresa")}
              variant="outlined"
            >
              {estados.map((estado) => (
                <MenuItem key={estado} value={estado}>
                  {estado}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {/* Botones */}
        <MDBox mt={4} display="flex" justifyContent="flex-end" gap={2}>
          <MDButton variant="outlined" color="secondary" onClick={handleCancel}>
            Cancelar
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={handleSave}>
            Guardar Cambios
          </MDButton>
        </MDBox>
      </MDBox>
    </MDModal>
  );
}

// Typechecking props for the MDEditModal
MDEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  empresa: PropTypes.object,
  onSave: PropTypes.func.isRequired,
};

export default MDEditModal; 